<template>
  <app-layout title="Detail Pengumuman Pemenang">
    <template #header>
      <div class="flex items-center justify-between">
        <h2 class="font-semibold text-xl text-gray-800 leading-tight">
          Detail Pengumuman Pemenang
        </h2>
        <button
          @click="goBack"
          class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:border-gray-700 focus:ring focus:ring-gray-200 active:bg-gray-600 disabled:opacity-25 transition"
        >
          <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
          </svg>
          Kembali
        </button>
      </div>
    </template>

    <div class="py-12">
      <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
        <!-- Winner Announcement Header -->
        <div class="bg-gradient-to-r from-green-50 to-yellow-50 border border-green-200 rounded-lg p-6 mb-6">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <i class="fas fa-trophy text-yellow-500 text-3xl"></i>
            </div>
            <div class="ml-4">
              <h3 class="text-lg font-medium text-green-800">Pengumuman Pemenang</h3>
              <p class="text-sm text-green-700">
                Pemenang telah ditetapkan dan pengumuman sedang berlangsung
              </p>
              <div class="mt-2">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <i class="fas fa-check-circle mr-1"></i>
                  Status: {{ dokumen.tahap?.nama_tahap || 'Pengumuman Pemenang' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Information -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Informasi Dokumen Pengadaan</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nama Paket</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.paket_pengadaan?.nama_paket_pengadaan || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Nomor BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ dokumen.nomor_bahp || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Tanggal BAHP</label>
              <p class="mt-1 text-sm text-gray-900">{{ formatDate(dokumen.tanggal_bahp) || '-' }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Status Dokumen</label>
              <p class="mt-1 text-sm">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="dokumen.status === 'selesai' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                >
                  <i v-if="dokumen.status === 'selesai'" class="pi pi-check-circle mr-1"></i>
                  {{ (dokumen.status || '-').toUpperCase() }}
                </span>
              </p>
            </div>
          </div>

          <!-- Allowed Actions -->
          <div class="mt-6 flex space-x-4">
            <Button 
              @click="showHPSModal"
              icon="pi pi-calculator"
              label="Lihat HPS"
              severity="info"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_baep && dokumen.tanggal_baep"
              @click="showBaepModal"
              icon="pi pi-file-pdf"
              label="Lihat BAEP"
              severity="success"
              size="small"
              text 
            />
            <Button 
              v-if="dokumen.nomor_bahp && dokumen.tanggal_bahp"
              @click="showBahpModal"
              icon="pi pi-file-pdf"
              label="Lihat BAHP"
              severity="success"
              size="small"
              text 
            />
            <Button 
              @click="goToSanggahView"
              icon="pi pi-comments"
              label="Lihat Sanggah"
              severity="warning"
              size="small"
              text 
            />
          </div>
        </div>

        <!-- Evaluasi Ulang History Alert -->
        <div v-if="evaluasiUlangHistory" class="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-lg shadow-sm">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-history text-amber-400 text-xl"></i>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-amber-800">
                Sanggah Evaluasi Ulang Diterima
              </h3>
              <div class="mt-2 text-sm text-amber-700">
                <p>
                  Sanggah telah diterima dengan opsi <strong>Evaluasi Ulang</strong> pada 
                  <strong>{{ formatDateTime(evaluasiUlangHistory.created_at) }}</strong> 
                  oleh <strong>{{ evaluasiUlangHistory.sanggah?.dokumen_pengadaan_actor?.user?.fullname || '-' }}</strong>
                </p>
                <p class="mt-2">
                  <strong>Catatan:</strong> {{ evaluasiUlangHistory.sanggah?.keterangan_pembatalan || '-' }}
                </p>
                <div class="mt-3">
                  <a href="#" @click.prevent="viewEvaluasiUlangHistory" class="text-amber-800 underline hover:text-amber-900 font-medium">
                    Lihat Detail Riwayat Evaluasi →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Winner Information -->
        <div v-if="pemenang" class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6 mb-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <i class="fas fa-trophy text-yellow-500 mr-2"></i>
            Informasi Pemenang
          </h3>
          <div class="bg-green-50 border border-green-200 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-green-700">Nama Perusahaan</label>
                <p class="mt-1 text-lg font-semibold text-green-900">{{ pemenang.penyedia?.nama_perusahaan || '-' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Total Penawaran</label>
                <p class="mt-1 text-lg font-semibold text-green-900">{{ formatCurrency(getTotalNilaiPenawaran(pemenang)) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Tanggal Penetapan</label>
                <p class="mt-1 text-sm text-green-900">{{ formatDate(pemenang.tanggal_penetapan_pemenang) || '-' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-green-700">Selisih dengan HPS</label>
                <p class="mt-1 text-sm font-medium" 
                   :class="getTotalSelisih(pemenang) > 0 ? 'text-red-600' : 'text-green-600'">
                  {{ formatCurrency(getTotalSelisih(pemenang)) }}
                  ({{ getTotalPercentageDiff(pemenang) > 0 ? '+' : '' }}{{ getTotalPercentageDiff(pemenang).toFixed(2) }}%)
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Pengajuan List (read-only) -->
        <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Daftar Pengajuan</h3>
          
          <div v-if="pengajuan.length > 0">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Penyedia</th>
                    <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Nilai Penawaran</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lulus / Tidak</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keterangan</th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="(item, index) in sortedPengajuan" :key="item.id"
                      :class="item.is_pemenang ? 'bg-green-50 border-l-4 border-green-400' : ''">
                    <td class="px-6 py-4 whitespace-nowrap text-sm">{{ index + 1 }}</td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div class="text-sm font-medium text-gray-900">
                        {{ item.penyedia?.nama_perusahaan || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {{ formatCurrency(getTotalNilaiPenawaran(item)) }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <div v-if="item.penilaian_penyedia_dokumen_pengadaan && item.penilaian_penyedia_dokumen_pengadaan.length > 0" class="text-sm">
                        <div v-for="(penilaian, pidx) in item.penilaian_penyedia_dokumen_pengadaan" :key="pidx" class="mb-1">
                          <span :class="penilaian.is_lulus ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" 
                                class="px-2 py-1 text-xs font-medium rounded-full">
                            {{ penilaian.is_lulus ? 'Lulus' : 'Tidak Lulus' }}
                          </span>
                          <span class="text-xs text-gray-500 ml-1">
                            {{ penilaian.bobot_penilaian_dokumen_pengadaan ? penilaian.bobot_penilaian_dokumen_pengadaan.nama : '' }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="text-sm text-gray-500">-</div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="text-sm text-gray-900">
                        {{ item.keterangan_penilaian || '-' }}
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap">
                      <span v-if="item.is_pemenang" class="inline-flex items-center text-green-600 font-semibold">
                        <i class="fas fa-trophy mr-1 text-yellow-500"></i>
                        PEMENANG
                      </span>
                      <span v-else class="text-gray-500">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4 text-gray-500">
            Tidak ada pengajuan yang ditemukan
          </div>
        </div>
      </div>
    </div>

    <!-- HPS Modal -->
    <dialog-modal :show="hpsModalOpen" @close="closeHPSModal" :max-width="'7xl'">
      <template #title>
        HPS (Harga Perkiraan Sendiri)
      </template>
      <template #content>
        <div v-if="dokumen.detail_h_p_s_dokumen_pengadaan && dokumen.detail_h_p_s_dokumen_pengadaan.length > 0">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nama Barang/Jasa</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Volume</th>
                  <th class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Harga Satuan</th>
                  <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="(hps, index) in dokumen.detail_h_p_s_dokumen_pengadaan" :key="hps.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
                  <td class="px-6 py-4 text-sm text-gray-900">{{ hps.nama_barang_jasa }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.volume }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">{{ hps.satuan }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">{{ formatCurrency(hps.harga_satuan) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">{{ formatCurrency(hps.volume * hps.harga_satuan) }}</td>
                </tr>
                <tr class="bg-gray-50 font-semibold">
                  <td colspan="5" class="px-6 py-4 text-right text-sm text-gray-900">Total HPS:</td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {{ formatCurrency(getTotalHPS()) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          Data HPS tidak tersedia
        </div>
      </template>
      <template #footer>
        <button @click="closeHPSModal" class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700">Kembali</button>
      </template>
    </dialog-modal>

    <!-- BAEP Modal -->
    <dialog-modal :show="baepModalOpen" @close="closeBaepModal" :max-width="'7xl'" :closeable="false">
      <template #title>Berita Acara Evaluasi Penawaran</template>
      <template #content>
        <BeritaAcaraEvaluasiPenawaran :dokumen_pengadaan_id="dokumen.id" />
      </template>
      <template #footer>
        <div class="flex justify-end space-x-4">
          <button @click="closeBaepModal" class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700">Kembali</button>
        </div>
      </template>
    </dialog-modal>

    <!-- BAHP Modal -->
    <dialog-modal :show="bahpModalOpen" @close="closeBahpModal" :max-width="'7xl'" :closeable="false">
      <template #title>Berita Acara Hasil Penawaran</template>
      <template #content>
        <BeritaAcaraHasilPenawaran :dokumen_pengadaan_id="dokumen.id" />
      </template>
      <template #footer>
        <div class="flex justify-end space-x-4">
          <button @click="closeBahpModal" class="inline-flex items-center px-4 py-2 bg-gray-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700">Kembali</button>
        </div>
      </template>
    </dialog-modal>

    <Toast position="top-right" />
  </app-layout>
  </template>

<script>
import { defineComponent } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import DialogModal from '@/Jetstream/DialogModal.vue'
import Button from 'primevue/button'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import BeritaAcaraEvaluasiPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraEvaluasiPenawaran.vue'
import BeritaAcaraHasilPenawaran from '@/Components/DokumenPengadaan/BeritaAcaraHasilPenawaran.vue'

export default defineComponent({
  components: { AppLayout, DialogModal, Button, Toast, BeritaAcaraEvaluasiPenawaran, BeritaAcaraHasilPenawaran },
  props: { dokumen: Object, pengajuan: Array, evaluasiUlangHistory: Object },
  setup() { const toast = useToast(); return { toast } },
  data() { return { hpsModalOpen: false, baepModalOpen: false, bahpModalOpen: false } },
  computed: {
    pemenang() { return this.pengajuan.find(p => p.is_pemenang) || null; },
    sortedPengajuan() {
      if (!Array.isArray(this.pengajuan)) return [];
      return [...this.pengajuan].sort((a,b)=>{
        const aL = this.isLulusSemua(a), bL = this.isLulusSemua(b);
        if (aL && !bL) return -1; if (!aL && bL) return 1;
        if (aL && bL) { const at = this.getTotalNilaiPenawaran(a)||0, bt = this.getTotalNilaiPenawaran(b)||0; return at-bt; }
        return 0;
      });
    }
  },
  methods: {
    goBack() { this.$inertia.visit(route('dokumen_pengadaan.list_pengumuman_pemenang_exec')) },
    goToSanggahView() { this.$inertia.visit(route('sanggah.view', this.dokumen.id)) },
    viewEvaluasiUlangHistory() {
      if (this.evaluasiUlangHistory && this.evaluasiUlangHistory.id) {
        const url = route('riwayat-evaluasi.view', {
          dokumenPengadaanId: this.dokumen.id,
          riwayatId: this.evaluasiUlangHistory.id
        });
        this.$inertia.visit(url);
      }
    },
    formatDate(d){ if(!d) return null; return new Date(d).toLocaleDateString('id-ID',{day:'2-digit',month:'long',year:'numeric'}) },
    formatDateTime(dt){ if(!dt) return '-'; const d = new Date(dt); const dp = d.toLocaleDateString('id-ID',{day:'2-digit',month:'long',year:'numeric'}); const tp = d.toLocaleTimeString('id-ID',{hour:'2-digit',minute:'2-digit',hour12:false}); return `${dp} ${tp} WIB`; },
    formatCurrency(v){ if(v==null) return 'Rp 0'; return new Intl.NumberFormat('id-ID',{style:'currency',currency:'IDR',minimumFractionDigits:0,maximumFractionDigits:0}).format(v) },
    getTotalNilaiPenawaran(p){ if(!p.detail_penawaran_kepersertaan_pengadaan) return 0; return p.detail_penawaran_kepersertaan_pengadaan.reduce((s,i)=>s+(i.volume*i.harga_satuan),0) },
    getTotalHPS(){ if(!this.dokumen.detail_h_p_s_dokumen_pengadaan) return 0; return this.dokumen.detail_h_p_s_dokumen_pengadaan.reduce((s,i)=>s+(i.volume*i.harga_satuan),0) },
    getTotalSelisih(p){ return this.getTotalNilaiPenawaran(p) - this.getTotalHPS() },
    getTotalPercentageDiff(p){ const h = this.getTotalHPS(); if(h===0) return 0; return ((this.getTotalNilaiPenawaran(p)-h)/h)*100 },
    isLulusSemua(p){ const l=p?.penilaian_penyedia_dokumen_pengadaan; if(!Array.isArray(l)||l.length===0) return false; return l.every(x=>x?.is_lulus===true) },
    showHPSModal(){ this.hpsModalOpen=true }, closeHPSModal(){ this.hpsModalOpen=false },
    showBaepModal(){ this.baepModalOpen=true }, closeBaepModal(){ this.baepModalOpen=false },
    showBahpModal(){ this.bahpModalOpen=true }, closeBahpModal(){ this.bahpModalOpen=false },
  }
})
</script>

<style scoped>
.bg-gradient-to-r { background: linear-gradient(to right, var(--tw-gradient-stops)); }
</style>

